import { observer } from "mobx-react-lite";
import { FC } from "react";

import {
  dataAttribute,
  DataAttributes,
  FontSizes,
  IconButton,
  IContextualMenuItem,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import {
  MedicalHistoryClinicalDataItemDto,
  MedicalHistoryTestElements
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import {
  AddSecGroupAccessRequestDto,
  Permission
} from "@libs/gateways/core/CoreGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { editMedicalHistoryItem } from "./utils.ts";

interface MedicalHistoryLisItemMenuProps {
  item: MedicalHistoryClinicalDataItemDto;
  clinicalRecord: ClinicalRecord;
  onDeleteDiagnosis: (item: MedicalHistoryClinicalDataItemDto) => void;
  openMedicalHistoryDialog: (value: MedicalHistoryClinicalDataItemDto) => void;
}

export const MedicalHistoryListItemMenu: FC<MedicalHistoryLisItemMenuProps> =
  observer(
    ({ clinicalRecord, onDeleteDiagnosis, openMedicalHistoryDialog, item }) => {
      const { core } = useStores();

      const { checkDiagnoseEditability } = usePatientRecordScreenContext();

      const onStatusChange = async (
        item: MedicalHistoryClinicalDataItemDto
      ) => {
        if (item.id) {
          const currentMedicalHistoryItem =
            clinicalRecord.medicalHistories.find(x => x.id === item.id);

          if (currentMedicalHistoryItem) {
            const clinicalData = editMedicalHistoryItem({
              clinicalRecord,
              id: item.id,
              medicalHistoryItem: {
                ...currentMedicalHistoryItem,
                active: !currentMedicalHistoryItem.active
              }
            });

            await clinicalRecord.saveClinicalData(clinicalData);
          }
        }
      };

      const onUpdateConfidentiality = async (
        item: MedicalHistoryClinicalDataItemDto
      ) => {
        if (item.id) {
          const currentMedicalHistoryItem =
            clinicalRecord.medicalHistories.find(x => x.id === item.id);

          if (currentMedicalHistoryItem) {
            const clinicalData = editMedicalHistoryItem({
              clinicalRecord,
              id: item.id,
              medicalHistoryItem: {
                ...currentMedicalHistoryItem,
                secGroupId: !!currentMedicalHistoryItem.secGroupId
                  ? undefined
                  : core.user?.privateSecGroupId
              }
            });

            await clinicalRecord.saveClinicalData(clinicalData);
          }
        }
      };

      const onItemContextMenu = (
        _item: MedicalHistoryClinicalDataItemDto
      ): IContextualMenuItem[] => {
        const isDiagnoseEditable =
          checkDiagnoseEditability &&
          checkDiagnoseEditability(_item?.episodeOfCareId);

        const items: IContextualMenuItem[] = [
          {
            key: "Edit",
            name: "Edit details",
            onClick: () => {
              openMedicalHistoryDialog(_item);
            }
          },
          {
            key: "active",
            name: `Make ${_item.active ? "Inactive" : "Active"}`,
            disabled: !isDiagnoseEditable,
            onClick: () => {
              onStatusChange(_item);
            }
          }
        ];

        if (core.hasPermissions(Permission.ConfidentialDataAllowed)) {
          items.push({
            key: "Confidential",
            name: !!_item.secGroupId
              ? "Remove confidentiality"
              : "Make confidential",
            onClick: () => {
              onUpdateConfidentiality(_item);
            }
          });
        }

        items.push({
          key: "Delete",
          name: "Delete",
          onClick: () => {
            onDeleteDiagnosis(_item);
          }
        });

        return items;
      };

      let itemDisabled = false;

      if (!!item.secGroupId && !!core.user?.id) {
        core.secGroupAccessRequestMap.forEach(secGroupAccessRequest => {
          if (
            secGroupAccessRequest.userId === core.user?.id &&
            secGroupAccessRequest.secGroupId === item.secGroupId &&
            !secGroupAccessRequest.status
          ) {
            itemDisabled = true;
          }
        });
      }

      return (
        <Stack horizontalAlign="center" verticalAlign="center">
          {!core.hasAccessToSecGroup(item.secGroupId) ? (
            <TooltipHost
              content={
                itemDisabled
                  ? "User already has an active request to access this data"
                  : "Request access"
              }
            >
              <IconButton
                iconProps={{ iconName: "ShieldAlert" }}
                disabled={itemDisabled}
                onClick={async () => {
                  if (!core?.user?.id) {
                    throw new Error("User undefined");
                  }

                  if (!item.secGroupId) {
                    throw new Error("SecGroup undefined");
                  }

                  const args: AddSecGroupAccessRequestDto = {
                    userId: core.user.id,
                    secGroupId: item.secGroupId
                  };

                  await core.addSecGroupAccessRequestStatus(args);
                }}
                styles={{
                  root: {
                    margin: "auto 4px",
                    fontSize: FontSizes.size16
                  }
                }}
              />
            </TooltipHost>
          ) : (
            <IconButton
              {...dataAttribute(
                DataAttributes.Element,
                MedicalHistoryTestElements.MoreButton
              )}
              iconProps={{
                iconName: "More"
              }}
              onRenderMenuIcon={() => null}
              menuProps={{ items: onItemContextMenu(item) }}
              styles={{
                root: { width: "32px", height: "36px", padding: 0 },
                flexContainer: { width: "32px", height: "36px" }
              }}
            />
          )}
        </Stack>
      );
    }
  );
